import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { CheckCircle } from 'lucide-react';

import Button from 'components/_UI/Button';
import Modal from 'components/_UI/Modal';
import Text from 'components/_UI/Text';
import partnerOnboarding from 'api/requests/partnerOnboarding';
import dateHelper from 'helpers/date';
import unformat from 'helpers/unformat';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import paths from 'routes/paths';
import { Column } from 'styled/alignment/Column';

import PartnerStepper from '../PartnerStepper';
import partnerDraft from '../partnerDraft';
import StepAccount from '../steps/StepAccount';
import StepBusiness from '../steps/StepBusiness';
import StepDiscovery from '../steps/StepDiscovery';
import StepDocuments from '../steps/StepDocuments';

const STEPS = [
    { id: 'business', label: 'Seu negócio' },
    { id: 'account', label: 'Seus dados' },
    { id: 'documents', label: 'Documento' },
    { id: 'discovery', label: 'Como conheceu' },
];

const STEP = {
    BUSINESS: 1,
    ACCOUNT: 2,
    DOCUMENTS: 3,
    DISCOVERY: 4,
};

/* A senha só existe em memória (ver `partnerDraft`), então este é o passo mais
   à frente que um refresh pode restaurar sem deixar campo obrigatório vazio. */
const MAX_RESTORABLE_STEP = STEP.ACCOUNT;

/**
 * Cadastro de parceiro.
 *
 * A ordem dos passos mudou: categoria e negócio → dados pessoais e senha →
 * documento e termos → como conheceu o Whizr (e é aí que o cadastro é
 * enviado). Antes a categoria (que decide se o CNPJ é exigido) era a última
 * pergunta, e a foto do documento — o pedido de maior atrito — vinha no
 * meio, antes de a pessoa ter investido qualquer coisa no cadastro.
 *
 * Este componente é o dono dos dados: os passos só coletam e devolvem. Foi o que
 * permitiu que voltar um passo não apague mais os arquivos já enviados.
 */
export default function PartnerForm() {
    const containerRef = useRef(null);

    const [currentStep, setCurrentStep] = useState(STEP.BUSINESS);
    const [business, setBusiness] = useState(null);
    const [account, setAccount] = useState(null);
    const [documents, setDocuments] = useState({ front: null, back: null });
    const [discovery, setDiscovery] = useState(null);

    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [documentErrors, setDocumentErrors] = useState({ front: '', back: '' });
    const [submittedEmail, setSubmittedEmail] = useState('');

    useEffect(() => {
        const draft = partnerDraft.read(MAX_RESTORABLE_STEP);

        if (!draft) {
            return;
        }

        setBusiness(draft.business || null);
        setAccount(draft.account || null);
        setCurrentStep(draft.step || STEP.BUSINESS);
    }, []);

    useEffect(() => {
        containerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }, [currentStep]);

    function persist(nextStep, nextBusiness, nextAccount) {
        partnerDraft.save({
            step: nextStep,
            business: nextBusiness ?? business,
            account: nextAccount ?? account,
        });
    }

    function onBusinessSubmit(values) {
        setBusiness(values);
        setCurrentStep(STEP.ACCOUNT);
        persist(STEP.ACCOUNT, values, null);
    }

    function onAccountSubmit(values) {
        setAccount(values);
        setCurrentStep(STEP.DOCUMENTS);
        persist(STEP.DOCUMENTS, null, values);
    }

    function goBack() {
        setSubmitError('');
        setCurrentStep((step) => Math.max(step - 1, STEP.BUSINESS));
    }

    function onDocumentsChange(next) {
        setDocuments(next);
        setDocumentErrors({ front: '', back: '' });
        setSubmitError('');
    }

    function buildPayload(discoveryValues) {
        const formData = new FormData();

        formData.append('full_name', `${account.firstName} ${account.lastName}`.trim());
        formData.append('email', account.email);
        // A máscara deixa `_` nas posições não preenchidas; o backend recebe o
        // mesmo formato de antes, só que sem o resto do placeholder.
        formData.append('phone', (account.phone || '').replace(/_/g, ''));
        formData.append('birth_date', dateHelper.unformat(account.birthDate));
        formData.append('document', unformat.getNumbers(account.document));
        formData.append('document_front_image', documents.front);
        formData.append('document_back_image', documents.back);
        formData.append('company_name', business.companyName);
        formData.append('partner_type', business.partnerType);
        formData.append('password', account.password);
        formData.append('password_confirmation', account.confirmPassword);

        if (business.partnerType === 'Establishment' && business.cnpj) {
            formData.append('has_cnpj', 1);
            formData.append('cnpj', business.cnpj);
        } else {
            formData.append('has_cnpj', 0);
        }

        const affiliateCode = localStorage.getItem('affiliateCode')?.trim().toUpperCase();

        if (affiliateCode) {
            formData.append('affiliate_code', affiliateCode);
        }

        formData.append('referral_source', discoveryValues.referralSource);

        if (discoveryValues.referralSource === 'outro') {
            formData.append('referral_source_other', discoveryValues.referralSourceOther);
        }

        return formData;
    }

    /* Documento e envio final eram um passo só: a validação do documento
       continua aqui, ligada ao upload que ela confere, e só avança para a
       pergunta de descoberta quando os arquivos são aceitos. */
    async function validateDocuments() {
        setSubmitting(true);
        setSubmitError('');
        setDocumentErrors({ front: '', back: '' });

        try {
            const validation = new FormData();
            validation.append('document_front_image', documents.front);
            validation.append('document_back_image', documents.back);

            await partnerOnboarding.validate(validation);
            setCurrentStep(STEP.DISCOVERY);
        } catch ({ response }) {
            const mapped = mapDocumentError(response);
            setDocumentErrors({ front: mapped.front, back: mapped.back });
            setSubmitError(mapped.general);
        } finally {
            setSubmitting(false);
        }
    }

    async function submitOnboarding(discoveryValues) {
        setDiscovery(discoveryValues);
        setSubmitting(true);
        setSubmitError('');

        try {
            await partnerOnboarding.create(buildPayload(discoveryValues));

            setSubmittedEmail(account.email);
            partnerDraft.clear();
            localStorage.removeItem('affiliateCode');
        } catch ({ response }) {
            setSubmitError(translateCreateError(response));
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <ContainerForm ref={containerRef}>
            <Content>
                <StepperArea>
                    <StepCounter>Passo {currentStep} de {STEPS.length}</StepCounter>
                    <PartnerStepper steps={STEPS} currentStep={currentStep} />
                </StepperArea>

                {currentStep === STEP.BUSINESS && (
                    <StepBusiness
                        defaultValues={business}
                        onSubmit={onBusinessSubmit}
                    />
                )}

                {currentStep === STEP.ACCOUNT && (
                    <StepAccount
                        defaultValues={account}
                        onSubmit={onAccountSubmit}
                        onBack={goBack}
                    />
                )}

                {currentStep === STEP.DOCUMENTS && (
                    <StepDocuments
                        documents={documents}
                        onDocumentsChange={onDocumentsChange}
                        onSubmit={validateDocuments}
                        onBack={goBack}
                        submitting={submitting}
                        documentErrors={documentErrors}
                        submitError={submitError}
                    />
                )}

                {currentStep === STEP.DISCOVERY && (
                    <StepDiscovery
                        defaultValues={discovery}
                        onSubmit={submitOnboarding}
                        onBack={goBack}
                        submitting={submitting}
                        submitError={submitError}
                    />
                )}
            </Content>

            {submittedEmail && (
                <Modal isOpen={true}>
                    <Column padding='30px' marginTop='10px' marginBottom='20px'>
                        <SuccessIcon>
                            <CheckCircle size={38} strokeWidth={2} aria-hidden='true' />
                        </SuccessIcon>

                        <Text name='subtitle' color={colors.nightDark} textAlign='center'>
                            Cadastro enviado para análise
                        </Text>

                        <Text textAlign='center'>
                            Em até 48 horas úteis você recebe a resposta em <strong>{submittedEmail}</strong> —
                            aprovação ou o que ainda falta.
                        </Text>

                        <Text textAlign='center' name='small'>
                            Não precisa reenviar. Se o e-mail não chegar, confira a caixa de spam
                            antes de tentar de novo.
                        </Text>
                    </Column>

                    <Button path={paths.home}>Voltar para o início</Button>
                </Modal>
            )}
        </ContainerForm>
    );
}

/**
 * O servidor responde com o lado que falhou em inglês ("...documento (front)")
 * e sugere "tente novamente mais tarde" — quando o que resolve é tirar outra
 * foto. Aqui a mensagem vira acionável e gruda na caixa certa.
 */
function mapDocumentError(response) {
    const raw = response?.data?.message || '';
    const advice = 'Não conseguimos ler este documento. Tire outra foto com boa luz, sem corte nas bordas e sem reflexo.';

    if (/front|frente/i.test(raw)) {
        return { front: advice, back: '', general: '' };
    }

    if (/back|verso/i.test(raw)) {
        return { front: '', back: advice, general: '' };
    }

    return {
        front: '',
        back: '',
        general: raw || 'Não foi possível validar os documentos agora. Tente de novo em instantes.',
    };
}

function translateCreateError(response) {
    const errors = response?.data?.errors;

    if (response?.status === 422 && errors) {
        if (errors.document) {
            return 'Este CPF já está cadastrado como parceiro. Se a conta é sua, entre com ela.';
        }

        if (errors.email) {
            return 'Este e-mail já está cadastrado. Volte e informe outro.';
        }

        if (errors.cnpj) {
            return 'Este CNPJ já está cadastrado como parceiro.';
        }

        const first = Object.values(errors)[0];
        return Array.isArray(first) ? first[0] : String(first);
    }

    return 'Não foi possível enviar o cadastro agora. Tente de novo em instantes.';
}

const ContainerForm = styled(Column)`
    justify-content: flex-start;
    align-items: center;
    padding: 72px 24px 48px;
    overflow-y: auto;

    @media screen and (max-width: ${breakpoints.md}) {
        padding: 90px 20px 40px;
    }
`;

const Content = styled.div`
    width: 100%;
    max-width: 420px;
`;

const StepperArea = styled.div`
    margin-bottom: 28px;
`;

const StepCounter = styled.p`
    margin: 0 0 8px;
    font-size: 12px;
    font-weight: 600;
    color: ${colors.night};
    text-align: center;
`;

const SuccessIcon = styled.div`
    display: flex;
    justify-content: center;
    margin-bottom: 10px;
    color: ${colors.success};
`;
